import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import {
  Cpu, AlertCircle, AlertTriangle, CheckCircle2, XCircle, Plus, Trash2, Info,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import {
  TOPOLOGY_PRESETS, TOPOLOGY_BY_VALUE, THERMAL_PRESETS, THERMAL_PRESET_BY_VALUE,
  REDUNDANCY_OPTIONS, CRITICALITY_OPTIONS, DUTY_PATTERNS, EFFICIENCY_CURVE_LOAD_PERCENTS,
  DEFAULT_EFFICIENCY_CURVE, DEFAULT_INPUTS, DEFAULT_LOAD_ROWS, NUMERIC_FIELD_SPECS,
  makeLoadRow, validateField, topologySeedValues,
} from '../../lib/dcdcSizingTools';
import { dcdcSizingToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;
const SPEC_BY_KEY = Object.fromEntries(NUMERIC_FIELD_SPECS.map((s) => [s.key, s]));
const SAG_ONLY_KEYS = new Set(['openCircuitVoltageV', 'internalResistanceOhm', 'sagCurrentA']);

const fmt = (n, d = 1) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));

function activeScalarKeys(useSagModel) {
  return Object.keys(SPEC_BY_KEY).filter((k) => useSagModel || !SAG_ONLY_KEYS.has(k));
}

const DcDcConverterSizingCalculator = () => {
  const [viewMode, setViewMode] = useState('guided');
  const [topology, setTopology] = useState(DEFAULT_INPUTS.topology);
  const [thermalPreset, setThermalPreset] = useState(DEFAULT_INPUTS.thermalPreset);
  const [redundancyMode, setRedundancyMode] = useState(DEFAULT_INPUTS.redundancyMode);
  const [useSagModel, setUseSagModel] = useState(DEFAULT_INPUTS.useSagModel);
  const [deratePeakTrack, setDeratePeakTrack] = useState(DEFAULT_INPUTS.deratePeakTrack);
  const [applyDutyCycleCredit, setApplyDutyCycleCredit] = useState(DEFAULT_INPUTS.applyDutyCycleCredit);

  const [raw, setRaw] = useState(() => {
    const out = {};
    Object.keys(SPEC_BY_KEY).forEach((k) => { out[k] = String(DEFAULT_INPUTS[k]); });
    return out;
  });
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const [efficiencyRaw, setEfficiencyRaw] = useState(() => Object.fromEntries(DEFAULT_EFFICIENCY_CURVE.map((p) => [p.loadPercent, String(p.efficiencyPercent)])));

  const [rows, setRows] = useState(DEFAULT_LOAD_ROWS);
  const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const addRow = () => setRows((prev) => [...prev, makeLoadRow()]);

  // Switching topology re-seeds source (incl. sag model), output, candidate-converter
  // and thermal defaults for that preset -- still fully editable afterwards, per the
  // "never silently apply a default" rule (the seeded values are shown, not hidden).
  useEffect(() => {
    const preset = TOPOLOGY_BY_VALUE[topology];
    const seed = topologySeedValues(preset);
    setRaw((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(seed).map(([k, v]) => [k, String(v)])) }));
    setThermalPreset(preset.thermalPreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topology]);

  useEffect(() => {
    if (thermalPreset === 'custom') return;
    const t = THERMAL_PRESET_BY_VALUE[thermalPreset];
    setRaw((prev) => ({ ...prev, thermalT1C: String(t.t1C), thermalT2C: String(t.t2C), thermalT2PercentRating: String(t.t2PercentRating), maxAmbientC: String(t.t2C) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thermalPreset]);

  const activeKeys = useMemo(() => activeScalarKeys(useSagModel), [useSagModel]);

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const key of activeKeys) {
      const err = validateField(SPEC_BY_KEY[key], raw[key]);
      if (err) errors[key] = err;
      else parsed[key] = Number(raw[key]);
    }
    return { fieldErrors: errors, values: parsed };
  }, [activeKeys, raw]);
  const hasErrors = Object.keys(fieldErrors).length > 0;

  const outputNominalV = Number(raw.outputNominalVoltageV) || 1;

  const [results, setResults] = useState(null);
  const [rowErrorsById, setRowErrorsById] = useState({});
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  const buildPayload = () => ({
    topology, thermalPreset, redundancyMode, useSagModel, deratePeakTrack, applyDutyCycleCredit,
    ...Object.fromEntries(Object.keys(SPEC_BY_KEY).map((k) => [k, raw[k] === '' ? undefined : Number(raw[k])])),
    efficiencyCurve: EFFICIENCY_CURVE_LOAD_PERCENTS.map((lp) => ({ loadPercent: lp, efficiencyPercent: Number(efficiencyRaw[lp]) })),
    rows: rows.map((r) => ({
      id: r.id, name: r.name, inputMode: r.inputMode,
      continuousPowerW: Number(r.continuousPowerW) || 0, continuousCurrentA: Number(r.continuousCurrentA) || 0,
      peakInputMode: r.peakInputMode, peakPowerW: Number(r.peakPowerW) || 0, peakCurrentA: Number(r.peakCurrentA) || 0,
      peakDurationMs: Number(r.peakDurationMs) || 0, dutyCyclePercent: Number(r.dutyCyclePercent) || 0,
      dutyPattern: r.dutyPattern, criticality: r.criticality, coincidentWithPeak: !!r.coincidentWithPeak,
    })),
  });

  useEffect(() => {
    if (hasErrors) { setResults(null); setCalcError(null); return; }
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      dcdcSizingToolApi.calculate(buildPayload())
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setCalcError(null);
            setResults(outcome.results);
            setRowErrorsById(Object.fromEntries((outcome.results.rowValidation || []).filter((r) => !r.valid).map((r) => [r.id, r.errors])));
          }
        })
        .catch((e) => { if (requestId === requestIdRef.current) setCalcError(e.message); });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, values, topology, thermalPreset, redundancyMode, useSagModel, deratePeakTrack, applyDutyCycleCredit, efficiencyRaw, rows]);

  const numField = (key, { label, placeholder } = {}) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    return (
      <div className="space-y-2" key={key}>
        <Label htmlFor={`dcdc-${key}`} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {label || spec.label} {spec.unit && <span className="normal-case text-muted-foreground/70">({spec.unit})</span>}
        </Label>
        <Input
          id={`dcdc-${key}`}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          placeholder={placeholder}
          aria-invalid={!!error}
          data-testid={`dcdc-${key}-input`}
        />
        {error && (
          <p className="flex items-center gap-1 text-xs text-destructive" data-testid={`dcdc-${key}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  const rationale = (text) => (
    <p className="flex items-start gap-1.5 text-xs text-muted-foreground leading-relaxed">
      <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-autoxium-blue" /> {text}
    </p>
  );

  const hasSafetyCriticalLoad = rows.some((r) => r.criticality === 'safety_critical');

  return (
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <Cpu className="h-5 w-5 text-autoxium-blue" />
                DC-DC Converter Sizing Tool
              </CardTitle>
              <CardDescription>
                From a vehicle load list and source voltage behavior to a defensible
                minimum converter spec, with every margin and assumption traceable in
                the waterfall below.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={topology} onValueChange={setTopology}>
                <SelectTrigger className="w-64" data-testid="dcdc-topology-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TOPOLOGY_PRESETS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Tabs value={viewMode} onValueChange={setViewMode}>
                <TabsList data-testid="dcdc-view-mode-tabs">
                  <TabsTrigger value="guided" data-testid="dcdc-mode-guided">Guided</TabsTrigger>
                  <TabsTrigger value="expert" data-testid="dcdc-mode-expert">Expert</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        {viewMode === 'guided' && (
          <CardContent className="pt-0">
            {rationale(`${TOPOLOGY_BY_VALUE[topology].description} Guided mode explains why each margin exists as you go — switch to Expert for a denser layout and the sensitivity view. Both modes share the same calculation engine, so switching never changes a result.`)}
          </CardContent>
        )}
      </Card>

      <LoadListSection rows={rows} updateRow={updateRow} removeRow={removeRow} addRow={addRow} outputNominalV={outputNominalV} rowErrorsById={rowErrorsById} loadSummary={results?.loadSummary} viewMode={viewMode} />

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="technical-card">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Source (Battery) Voltage Behavior</CardTitle>
            {viewMode === 'guided' && <CardDescription>Do not use a single nominal voltage — the min/max window and dropout envelope drive feasibility, not just steady-state sizing.</CardDescription>}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {numField('sourceNominalVoltageV')}
              {numField('sourceMinVoltageV')}
              {numField('sourceMaxVoltageV')}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Checkbox id="dcdc-useSagModel" checked={useSagModel} onCheckedChange={(v) => setUseSagModel(!!v)} data-testid="dcdc-useSagModel-checkbox" />
              <Label htmlFor="dcdc-useSagModel" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Derive min voltage from sag model instead</Label>
            </div>
            {useSagModel && (
              <div className="grid sm:grid-cols-3 gap-4 p-3 bg-muted/50 rounded-md">
                {numField('openCircuitVoltageV')}
                {numField('internalResistanceOhm')}
                {numField('sagCurrentA')}
                {results && <p className="sm:col-span-3 text-xs text-muted-foreground">Derived min voltage: <span className={`font-mono font-bold ${results.sagCollapsed ? 'text-destructive' : 'text-foreground'}`}>{fmt(results.derivedSourceMinVoltageV, 2)} V</span> — solved from V = V<sub>oc</sub> − R × (I<sub>other</sub> + P<sub>in,converter</sub>/V), so the converter's own peak input power is part of the sag. Used instead of the Min Operating Voltage field above.</p>}
              </div>
            )}
            <div className="grid sm:grid-cols-3 gap-4 pt-2 border-t border-structure">
              {numField('transientDropoutFloorV')}
              {numField('transientDropoutDurationMs')}
              {numField('transientOvervoltageV')}
            </div>
            {viewMode === 'guided' && rationale('Transient dropout (e.g. cold-crank dip on 12V, heavy-load sag at low SOC on HV) drives the input-side ride-through / hold-up check below; the transient overvoltage peak (e.g. regen overshoot, load-dump residue) is checked against the converter\'s absolute-max input rating. Neither affects steady-state sizing.')}
          </CardContent>
        </Card>

        <Card className="technical-card">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Output Bus Requirements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {numField('outputNominalVoltageV')}
              {numField('outputRegulationTolerancePercent')}
              {numField('outputMinVoltageBeforeLoadShedV')}
              {numField('rippleRequirementMv')}
            </div>
            <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-structure">
              {numField('batteryChargeCurrentA')}
              {numField('lvBatteryPeakSharePercent')}
            </div>
            {viewMode === 'guided' && rationale('Output current ratings are quoted at the LOWER edge of the regulation band (worst case for constant-power loads); the band is also checked against the load-shed threshold. The LV buffer battery\'s charging current is a real continuous load on the converter, and during a peak the battery can supply part of the excess — leave that at 0% to size the converter for the full peak on its own. Ripple/noise is informational only.')}
          </CardContent>
        </Card>
      </div>

      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Efficiency Curve</CardTitle>
          <CardDescription>Editable efficiency (%) vs. converter load — used to back-calculate required input-side current/power.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {EFFICIENCY_CURVE_LOAD_PERCENTS.map((lp) => (
              <div className="space-y-2" key={lp}>
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{lp}% Load</Label>
                <Input
                  type="number" step="any" value={efficiencyRaw[lp]}
                  onChange={(e) => setEfficiencyRaw((prev) => ({ ...prev, [lp]: e.target.value }))}
                  className="font-mono" data-testid={`dcdc-efficiency-${lp}-input`}
                />
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-4">
            {numField('efficiencyPenaltyAtMinVoltagePts')}
          </div>
          {viewMode === 'guided' && <div className="pt-2">{rationale('Efficiency is read at the converter\'s actual operating point (design load ÷ rating), not at 100% — margins mean it normally runs well below rating, where light-load efficiency is lower. At min input voltage it drops by the penalty above (higher input current → higher conduction loss).')}</div>}
        </CardContent>
      </Card>

      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Design Margins</CardTitle>
          <CardDescription>Each margin is applied explicitly and sequentially — see the waterfall below for exactly how.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              {numField('safetyMarginPercent')}
              {rationale('Default 25%. Covers measurement uncertainty and calculation error — NOT future growth.')}
            </div>
            <div>
              {numField('growthMarginPercent')}
              {rationale('Default 15%. Facelift-cycle load growth — tracked separately from the safety margin so the two are never conflated.')}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-structure">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Thermal Derating Curve</Label>
              <Select value={thermalPreset} onValueChange={setThermalPreset}>
                <SelectTrigger data-testid="dcdc-thermal-preset-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {THERMAL_PRESETS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {numField('maxAmbientC', { label: thermalPreset === 'liquid_coolant' ? 'Max Coolant Inlet Temp' : 'Max Operating Ambient' })}
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {numField('thermalT1C', { label: 'T1 (100% Rating)' })}
            {numField('thermalT2C', { label: 'T2 (Elevated)' })}
            {numField('thermalT2PercentRating', { label: 'Rating Retained at T2' })}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {numField('thermalTimeConstantS')}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="dcdc-deratePeakTrack" checked={deratePeakTrack} onCheckedChange={(v) => setDeratePeakTrack(!!v)} data-testid="dcdc-deratePeakTrack-checkbox" />
            <Label htmlFor="dcdc-deratePeakTrack" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Force full thermal derating on the peak track</Label>
          </div>
          {viewMode === 'guided' && rationale('The peak track is derated according to the longest coincident peak vs. the converter\'s thermal time constant: f_peak = f_cont + (1 − f_cont)·e^(−t_peak/τ). A peak much shorter than τ is absorbed by thermal mass; one much longer is derated like continuous load. Tick the box to always apply the full continuous derating.')}
          <div className="flex items-center gap-2">
            <Checkbox id="dcdc-applyDutyCycleCredit" checked={applyDutyCycleCredit} onCheckedChange={(v) => setApplyDutyCycleCredit(!!v)} data-testid="dcdc-applyDutyCycleCredit-checkbox" />
            <Label htmlFor="dcdc-applyDutyCycleCredit" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Apply duty-cycle credit to the continuous rating</Label>
          </div>
          {viewMode === 'guided' && rationale('Intermittent/Cyclic rows count at their Duty % average toward the continuous THERMAL rating only (valid when their ON-time is short vs. the thermal time constant). Input current, hold-up and the peak track always use the full ON draw.')}

          <div className="pt-3 border-t border-structure space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Redundancy {hasSafetyCriticalLoad ? '' : '(no Safety-Critical load tagged — usually not needed)'}</Label>
            <Select value={redundancyMode} onValueChange={setRedundancyMode}>
              <SelectTrigger className="sm:w-96" data-testid="dcdc-redundancy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REDUNDANCY_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {redundancyMode === 'load_share_2up' && <div className="sm:w-96 pt-1">{numField('currentShareImbalancePercent')}</div>}
            {results?.warnings?.some((w) => w.includes('Load-share')) && (
              <p className="flex items-start gap-1.5 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> Load-Share alone isn't fail-operational for safety-critical loads — losing one unit leaves only 50% capacity.</p>
            )}
          </div>
          {viewMode === 'expert' && (
            <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-structure">
              {numField('sensitivityPerturbPercent')}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Voltage-Window Feasibility Inputs</CardTitle>
          <CardDescription>Seeded with a typical converter input range for the selected topology — edit to check a specific candidate converter's datasheet values.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {numField('candidateInputMinV')}
            {numField('candidateInputMaxV')}
            {numField('candidateInputAbsMaxV')}
            {numField('assumedHoldupCapacitanceUf', { placeholder: '0 = report required minimum' })}
          </div>
          {viewMode === 'guided' && rationale('Hold-up capacitance is on the converter INPUT: it must keep the input above the converter\'s UVLO (min input voltage) for the dropout duration, starting from the source min voltage. Only needed when the UVLO is above the dropout floor. Assumes the source is isolated from the capacitor during the dropout (e.g. by a diode or contactor).')}
        </CardContent>
      </Card>

      {calcError && (
        <div className="flex items-center gap-2 text-sm text-destructive" data-testid="dcdc-calc-error"><AlertCircle className="h-4 w-4 shrink-0" /> {calcError}</div>
      )}
      {hasErrors && !calcError && (
        <p className="text-sm text-muted-foreground" data-testid="dcdc-results-placeholder">Fix the highlighted fields above to see the sizing result.</p>
      )}

      {results && !hasErrors && (
        <ResultsSection
          results={results}
          outputNominalV={outputNominalV}
          viewMode={viewMode}
          perturbPercent={values.sensitivityPerturbPercent}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Load List
// ---------------------------------------------------------------------------

const LoadListSection = ({ rows, updateRow, removeRow, addRow, outputNominalV, rowErrorsById, loadSummary, viewMode }) => {
  const chartData = (loadSummary?.rows || []).map((r) => ({ name: r.name, Continuous: r.continuousW, Contributes: r.contributionW }));

  return (
    <Card className="technical-card">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Vehicle Electrical Load List</CardTitle>
          <CardDescription>One row per ECU/load. "Coincident With Peak" drives the worst-case simultaneous demand — not a naive sum of every peak.</CardDescription>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addRow} data-testid="dcdc-add-row-btn"><Plus className="h-3.5 w-3.5" /> Add Load</Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <Table data-testid="dcdc-load-table">
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[140px]">Name</TableHead>
                <TableHead className="min-w-[130px]">Criticality</TableHead>
                <TableHead className="min-w-[210px]">Continuous</TableHead>
                <TableHead className="min-w-[210px]">Peak</TableHead>
                <TableHead className="min-w-[90px]">Peak Dur. (ms)</TableHead>
                <TableHead className="min-w-[90px]">Duty %</TableHead>
                <TableHead className="min-w-[130px]">Duty Pattern</TableHead>
                <TableHead className="min-w-[90px]">Coincident<br />w/ Peak?</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const errs = rowErrorsById[row.id] || {};
                const contA = row.inputMode === 'power' ? (Number(row.continuousPowerW) || 0) / outputNominalV : Number(row.continuousCurrentA) || 0;
                const contW = row.inputMode === 'power' ? Number(row.continuousPowerW) || 0 : (Number(row.continuousCurrentA) || 0) * outputNominalV;
                const peakA = row.peakInputMode === 'power' ? (Number(row.peakPowerW) || 0) / outputNominalV : Number(row.peakCurrentA) || 0;
                const peakW = row.peakInputMode === 'power' ? Number(row.peakPowerW) || 0 : (Number(row.peakCurrentA) || 0) * outputNominalV;
                return (
                  <TableRow key={row.id} data-testid={`dcdc-row-${row.id}`}>
                    <TableCell>
                      <Input value={row.name} onChange={(e) => updateRow(row.id, { name: e.target.value })} placeholder="e.g. EPS" className="h-8 text-xs w-32" data-testid={`dcdc-name-${row.id}`} />
                    </TableCell>
                    <TableCell>
                      <Select value={row.criticality} onValueChange={(v) => updateRow(row.id, { criticality: v })}>
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`dcdc-criticality-${row.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{CRITICALITY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Select value={row.inputMode} onValueChange={(v) => updateRow(row.id, { inputMode: v })}>
                          <SelectTrigger className="h-8 w-16 text-xs" data-testid={`dcdc-cont-mode-${row.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="power">W</SelectItem><SelectItem value="current">A</SelectItem></SelectContent>
                        </Select>
                        <Input
                          type="number" step="any"
                          value={row.inputMode === 'power' ? row.continuousPowerW : row.continuousCurrentA}
                          onChange={(e) => updateRow(row.id, row.inputMode === 'power' ? { continuousPowerW: e.target.value } : { continuousCurrentA: e.target.value })}
                          className={`h-8 w-20 text-xs font-mono ${errs.continuousPowerW || errs.continuousCurrentA ? 'border-destructive' : ''}`}
                          data-testid={`dcdc-cont-value-${row.id}`}
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">{fmt(contW, 0)} W · {fmt(contA, 2)} A</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Select value={row.peakInputMode} onValueChange={(v) => updateRow(row.id, { peakInputMode: v })}>
                          <SelectTrigger className="h-8 w-16 text-xs" data-testid={`dcdc-peak-mode-${row.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="power">W</SelectItem><SelectItem value="current">A</SelectItem></SelectContent>
                        </Select>
                        <Input
                          type="number" step="any"
                          value={row.peakInputMode === 'power' ? row.peakPowerW : row.peakCurrentA}
                          onChange={(e) => updateRow(row.id, row.peakInputMode === 'power' ? { peakPowerW: e.target.value } : { peakCurrentA: e.target.value })}
                          className={`h-8 w-20 text-xs font-mono ${errs.peakPowerW || errs.peakCurrentA ? 'border-destructive' : ''}`}
                          data-testid={`dcdc-peak-value-${row.id}`}
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">{fmt(peakW, 0)} W · {fmt(peakA, 2)} A</p>
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="any" value={row.peakDurationMs} onChange={(e) => updateRow(row.id, { peakDurationMs: e.target.value })} className={`h-8 w-20 text-xs font-mono ${errs.peakDurationMs ? 'border-destructive' : ''}`} data-testid={`dcdc-peakdur-${row.id}`} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="any" value={row.dutyCyclePercent} onChange={(e) => updateRow(row.id, { dutyCyclePercent: e.target.value })} className={`h-8 w-16 text-xs font-mono ${errs.dutyCyclePercent ? 'border-destructive' : ''}`} data-testid={`dcdc-duty-${row.id}`} />
                    </TableCell>
                    <TableCell>
                      <Select value={row.dutyPattern} onValueChange={(v) => updateRow(row.id, { dutyPattern: v })}>
                        <SelectTrigger className="h-8 w-28 text-xs" data-testid={`dcdc-dutypattern-${row.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{DUTY_PATTERNS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox checked={!!row.coincidentWithPeak} onCheckedChange={(v) => updateRow(row.id, { coincidentWithPeak: !!v })} data-testid={`dcdc-coincident-${row.id}`} />
                    </TableCell>
                    <TableCell>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => removeRow(row.id)} aria-label="Remove row" data-testid={`dcdc-remove-${row.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {loadSummary && (
          <div className="grid lg:grid-cols-2 gap-4 pt-4 border-t border-structure">
            <div className="grid grid-cols-2 gap-3 text-xs font-mono content-start">
              <div className="p-3 bg-muted/50 rounded-md">
                <p className="text-muted-foreground uppercase tracking-wider text-[10px]">Total Continuous Demand</p>
                <p className="text-xl font-black text-foreground" data-testid="dcdc-summary-continuous">{fmt(loadSummary.continuousTotalW, 0)} W</p>
              </div>
              <div className="p-3 bg-muted/50 rounded-md">
                <p className="text-muted-foreground uppercase tracking-wider text-[10px]">Worst-Case Simultaneous Peak</p>
                <p className="text-xl font-black text-autoxium-blue" data-testid="dcdc-summary-peak">{fmt(loadSummary.worstCaseSimultaneousPeakW, 0)} W</p>
              </div>
              {viewMode === 'guided' && (
                <p className="col-span-2 text-[11px] text-muted-foreground normal-case">Naive sum of every row's peak would be {fmt(loadSummary.naivePeakTotalW, 0)} W — the simultaneity-aware figure above is lower because independent loads use their continuous draw, not their peak, in the worst-case total.</p>
              )}
            </div>
            {chartData.length > 0 && (
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
                    <RechartsTooltip formatter={(v) => `${fmt(v, 0)} W`} />
                    <Bar dataKey="Contributes" fill="#2563eb" radius={[0, 3, 3, 0]}>
                      <LabelList dataKey="Contributes" position="right" style={{ fontSize: 10 }} formatter={(v) => fmt(v, 0)} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

const CheckBadge = ({ ok, label }) => (
  <Badge variant="outline" className={`font-mono text-[10px] gap-1 ${ok ? 'text-emerald-700 border-emerald-600/40' : 'text-destructive border-destructive/40'}`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {label}
  </Badge>
);

const HOLDUP_METHOD_TEXT = {
  no_dropout: 'No dropout below the min operating voltage specified — nothing to ride through.',
  input_range: 'Rides through on its input range alone (UVLO at/below the dropout floor) — no hold-up capacitance needed.',
  holdup: 'Relies on input hold-up capacitance to stay above its UVLO during the dropout.',
  none: 'Cannot ride through the dropout as specified.',
};

const ResultsSection = ({ results, viewMode, perturbPercent }) => {
  const { waterfall, voltageWindow, holdup, finalSpec, inputSpec, warnings, sensitivity, excludedRows } = results;

  return (
    <div className="space-y-6" data-testid="dcdc-results">
      {excludedRows?.length > 0 && (
        <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/40 p-3 text-sm text-destructive" data-testid="dcdc-excluded-rows">
          <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span><span className="font-bold">Spec is UNDERSIZED:</span> {excludedRows.length} load row(s) ({excludedRows.map((r) => r.name).join(', ')}) have invalid values and are excluded from the sizing. Fix the highlighted cells in the load list.</span>
        </div>
      )}
      {warnings?.length > 0 && (
        <div className="space-y-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 p-3" data-testid="dcdc-warnings">
          {warnings.map((w, i) => (
            <p key={i} className="flex items-start gap-1.5 text-xs text-amber-800 leading-relaxed"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w}</p>
          ))}
        </div>
      )}

      <Card className="technical-card ring-1 ring-autoxium-blue/30">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Final Minimum Spec</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Min Continuous Output Rating</p>
              <p className="font-mono text-3xl font-black text-autoxium-blue" data-testid="dcdc-final-continuous">{fmt(finalSpec.minContinuousOutputPowerW, 0)} W</p>
              <p className="text-xs text-muted-foreground">{fmt(finalSpec.minContinuousOutputCurrentA, 2)} A at {fmt(finalSpec.outputCurrentReferenceV, 2)} V (min regulated output)</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Min Peak/Transient Rating</p>
              <p className="font-mono text-3xl font-black text-foreground" data-testid="dcdc-final-peak">{fmt(finalSpec.minPeakOutputPowerW, 0)} W</p>
              <p className="text-xs text-muted-foreground">{fmt(finalSpec.minPeakOutputCurrentA, 2)} A, sustained {fmt(finalSpec.peakDurationMs, 0)} ms (longest coincident peak)</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <CheckBadge ok={voltageWindow.overallOk} label="Voltage-Window Feasibility" />
            <Badge variant="outline" className="font-mono text-[10px]">{finalSpec.redundancyLabel}</Badge>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t border-structure text-xs font-mono">
            <div className="flex justify-between gap-2"><span className="text-muted-foreground">Required Input Voltage Range</span><span className="font-bold">{fmt(finalSpec.requiredInputVoltageRangeV[0], 1)}–{fmt(finalSpec.requiredInputVoltageRangeV[1], 1)} V</span></div>
            <div className="flex justify-between gap-2"><span className="text-muted-foreground">Required Abs-Max Input</span><span className="font-bold">≥ {fmt(finalSpec.candidateInputAbsMaxV, 1)} V</span></div>
            <div className="flex justify-between gap-2"><span className="text-muted-foreground">Output Regulation Tolerance</span><span className="font-bold">±{fmt(finalSpec.outputRegulationTolerancePercent, 1)}%</span></div>
            <div className="flex justify-between gap-2"><span className="text-muted-foreground">Thermal Operating Point</span><span className="font-bold">{fmt(finalSpec.maxAmbientC, 0)} °C</span></div>
            <div className="flex justify-between gap-2"><span className="text-muted-foreground">Design Load Drawn (cont. / peak)</span><span className="font-bold">{fmt(finalSpec.designContinuousLoadW, 0)} / {fmt(finalSpec.designPeakLoadW, 0)} W</span></div>
            <div className="flex justify-between gap-2"><span className="text-muted-foreground">Operating Point (cont. / peak)</span><span className="font-bold">{fmt(inputSpec.continuousLoadPercent, 0)}% / {fmt(inputSpec.peakLoadPercent, 0)}% of rating</span></div>
          </div>
          <div className="pt-3 border-t border-structure overflow-x-auto">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Input Side (total, from design load — for source fuse / harness sizing)</p>
            <Table data-testid="dcdc-input-table">
              <TableHeader>
                <TableRow>
                  <TableHead />
                  <TableHead className="text-right">At Min Voltage</TableHead>
                  <TableHead className="text-right">At Nominal Voltage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[['Continuous', inputSpec.continuous], ['Peak', inputSpec.peak]].map(([label, track]) => (
                  <TableRow key={label}>
                    <TableCell className="text-xs font-bold">{label}</TableCell>
                    {[track.atMinVoltage, track.atNominalVoltage].map((pt, i) => (
                      <TableCell key={i} className="text-right font-mono text-xs">
                        <span className="font-bold">{fmt(pt.inputCurrentA, 2)} A</span>
                        <span className="text-muted-foreground"> · {fmt(pt.inputPowerW, 0)} W · η {fmt(pt.efficiencyPercent, 1)}%</span>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-sm">Margin Waterfall (Audit Trail)</CardTitle>
          <CardDescription>Continuous and peak tracks run in parallel — never averaged together.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="dcdc-waterfall-table">
            <TableHeader>
              <TableRow>
                <TableHead>Stage</TableHead>
                <TableHead className="text-right">Continuous (W)</TableHead>
                <TableHead className="text-right">Peak (W)</TableHead>
                {viewMode === 'expert' && <TableHead>Rationale</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {waterfall.stages.map((s, i) => (
                <TableRow key={i} className={i === waterfall.stages.length - 1 ? 'bg-autoxium-blue/5' : ''}>
                  <TableCell className="text-xs font-bold">{s.stage}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(s.continuousW, 1)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(s.peakW, 1)}</TableCell>
                  {viewMode === 'expert' && <TableCell className="text-xs text-muted-foreground">{s.note}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight text-sm">Voltage-Window Feasibility Check</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            <CheckBadge ok={voltageWindow.spanOk} label="Spans Source Window" />
            <CheckBadge ok={voltageWindow.dropoutOk} label="Rides Through Dropout" />
            <CheckBadge ok={voltageWindow.overvoltageOk} label="Survives Transient Overvoltage" />
          </div>
          <p className="text-xs text-muted-foreground">{HOLDUP_METHOD_TEXT[holdup.method]}</p>
          {voltageWindow.messages.map((m, i) => (
            <p key={i} className="flex items-start gap-1.5 text-xs text-destructive leading-relaxed"><AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {m}</p>
          ))}
          {holdup.requiredUf !== null && (
            <p className="text-xs text-muted-foreground pt-2 border-t border-structure">
              Required minimum INPUT hold-up capacitance: <span className="font-mono font-bold text-foreground">{fmt(holdup.requiredUf, 1)} µF</span> (C = 2·P<sub>in</sub>·t / (V<sub>src,min</sub>² − V<sub>UVLO</sub>²), at {fmt(holdup.inputPowerW, 0)} W peak input).
              {holdup.assumedUf && (
                <> Assumed available ({fmt(holdup.assumedUf, 1)} µF) {holdup.ok ? <span className="text-emerald-700 font-bold">meets</span> : <span className="text-destructive font-bold">does NOT meet</span>} this requirement.</>
              )}
            </p>
          )}
        </CardContent>
      </Card>

      {viewMode === 'expert' && sensitivity && (
        <Card className="technical-card">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Sensitivity — What Moves the Spec Most</CardTitle>
            <CardDescription>Each input perturbed ±{perturbPercent}% (temperature: ±{perturbPercent}% of the derating curve's T1→T2 span); ranked by the larger % change in continuous or peak input current at min source voltage. Worth double-checking before sign-off.</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ height: Math.max(160, sensitivity.targets.length * 32) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sensitivity.targets} layout="vertical" margin={{ left: 8, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} unit="%" />
                  <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 10 }} />
                  <RechartsTooltip formatter={(v) => `${fmt(v, 2)}%`} />
                  <Bar dataKey="deltaPercent" radius={[0, 3, 3, 0]}>
                    {sensitivity.targets.map((_, i) => <Cell key={i} fill={i < 3 ? '#dc2626' : '#2563eb'} />)}
                    <LabelList dataKey="deltaPercent" position="right" style={{ fontSize: 10 }} formatter={(v) => `${fmt(v, 1)}%`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
      {viewMode === 'guided' && (
        <p className="text-xs text-muted-foreground text-center">Switch to Expert mode above to see the sensitivity (tornado) view used for design-review sign-off.</p>
      )}
    </div>
  );
};

export default DcDcConverterSizingCalculator;
